import { createContext, useContext, type ReactNode } from 'react';

/**
 * LE PORT DES CONVERSATIONS AVEC LIA (chantier `centre-communication`).
 *
 * Le centre de communication du shell garde PLUSIEURS conversations avec Lia,
 * une par sujet, et leur historique survit au rechargement et au changement de
 * poste : il vit en base, dans un document par sujet, sous le chemin de son
 * auteur (`orgs/{orgId}/liaConversations/{uid}/sujets/{sujetId}`, rules a
 * l'avenant). Le shell n'a pas le SDK : c'est l'hote qui monte ce port, comme
 * les reglages IA (`IaSettingsPort`), et le shell ne connait que ces vues.
 *
 * Un sujet n'existe en base qu'a partir de son PREMIER message : le « + » du
 * centre ouvre un sujet en memoire, et c'est `enregistrer` qui l'ecrit. Zero
 * document vide.
 */
export interface LiaFicheSuivieView {
  appId: string;
  doorId: string;
  recordId: string;
  /** Le titre tel qu'il etait quand la conversation a commence a suivre la fiche. */
  titre: string;
}

/** Un tour conserve : ce qui se relit, jamais les etapes d'outils en entier. */
export interface LiaTourView {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** Instant d'envoi ou de reponse, millisecondes depuis l'epoque. Absent sur un tour d'avant ce chantier. */
  at?: number;
  /** Les fiches que ce tour a citees, pour les rouvrir : titre, app, porte, id. */
  records?: { id: string; title: string; appId: string; doorId: string; doorName?: string; family?: string }[];
  /** Le resume des outils appeles : combien, et de quelle nature. */
  outils?: { lectures: number; ecritures: number; echecs: number };
}

export interface LiaSujetView {
  id: string;
  titre: string;
  /** L'identifiant que le serveur de Lia connait pour ce fil (sa memoire courte). */
  conversationId: string;
  tours: LiaTourView[];
  fiche: LiaFicheSuivieView | null;
  creeLe: number;
  modifieLe: number;
  archiveLe: number | null;
  /** Les `personId` avec qui le sujet est partage (lecture seule chez eux). */
  partageA?: string[] | undefined;
}

/** Un sujet qu'une autre personne partage avec moi : son proprietaire, et lecture seule. */
export interface LiaSujetPartageView extends LiaSujetView {
  /** L'uid du proprietaire. */
  uid: string;
}

export interface LiaConversationsPort {
  observerReponses?(uid: string, sujetId: string, recevoir: (r: Array<{ id: string; texte: string; at: number; docId?: string }>) => void, erreur?: (e: unknown) => void): () => void;
  /** Les sujets de la personne connectee, archives compris, du plus recent au plus ancien. */
  observer(onValue: (sujets: LiaSujetView[]) => void, onError?: (error: unknown) => void): () => void;
  /** Ecrit (ou cree) un sujet entier : titre, tours bornes, fiche suivie. */
  enregistrer(sujet: Omit<LiaSujetView, 'creeLe' | 'modifieLe' | 'archiveLe'>): Promise<void>;
  renommer(id: string, titre: string): Promise<void>;
  archiver(id: string): Promise<void>;
  desarchiver(id: string): Promise<void>;
  /** Partage en lecture seule avec des personnes de l'org (`personId`) ; absent : l'hote ne sait pas partager. */
  partager?(id: string, personIds: readonly string[]): Promise<void>;
  retirerPartage?(id: string, personIds: readonly string[]): Promise<void>;
  /** Les sujets que d'autres partagent avec moi, en lecture seule. */
  observerPartagesAvecMoi?(onValue: (sujets: LiaSujetPartageView[]) => void, onError?: (error: unknown) => void): () => void;
}

const Context = createContext<LiaConversationsPort | null>(null);

export function LiaConversationsProvider({ value, children }: { value: LiaConversationsPort; children: ReactNode }) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

/** `null` quand l'hote ne monte pas le port : les sujets ne vivent alors que le temps de la page. */
export function useLiaConversationsPort(): LiaConversationsPort | null {
  return useContext(Context);
}
